/**
 * OpfsBlobStore — a BlobStore over the browser's Origin Private File System.
 *
 * Rooted at a directory path (e.g. `["sites", siteId]`). Keys are relative
 * paths under that root (`_assets/<sha>`), so a draft's local bucket has the
 * exact key shape as the cloud bucket and the export dir. Main-thread async
 * OPFS only (no SharedArrayBuffer / sync handles).
 */
import type { BlobStore } from "./index.js";

function opfsAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.storage?.getDirectory === "function"
  );
}

export class OpfsBlobStore implements BlobStore {
  /** @param root directory segments under the OPFS root, e.g. ["sites", id]. */
  constructor(private readonly root: ReadonlyArray<string>) {}

  /** Resolve a directory handle, optionally creating the chain. */
  private async dir(
    create: boolean,
    segments: ReadonlyArray<string>,
  ): Promise<FileSystemDirectoryHandle | null> {
    if (!opfsAvailable()) return null;
    let h = await navigator.storage.getDirectory();
    for (const seg of [...this.root, ...segments]) {
      const next = await h
        .getDirectoryHandle(seg, { create })
        .catch(() => null);
      if (!next) return null;
      h = next;
    }
    return h;
  }

  /** Split a key into [...dirSegments, fileName]. */
  private split(key: string): { dirs: string[]; file: string } {
    const parts = key.split("/").filter(Boolean);
    const file = parts.pop() ?? key;
    return { dirs: parts, file };
  }

  private async fileHandle(
    key: string,
    create: boolean,
  ): Promise<FileSystemFileHandle | null> {
    const { dirs, file } = this.split(key);
    const dir = await this.dir(create, dirs);
    if (!dir) return null;
    return dir.getFileHandle(file, { create }).catch(() => null);
  }

  async has(key: string): Promise<boolean> {
    return (await this.fileHandle(key, false)) !== null;
  }

  async getBlob(key: string): Promise<Blob | null> {
    const h = await this.fileHandle(key, false);
    if (!h) return null;
    return h.getFile().catch(() => null);
  }

  async get(key: string): Promise<Uint8Array | null> {
    const blob = await this.getBlob(key);
    return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
  }

  async put(key: string, data: Uint8Array | Blob): Promise<void> {
    const h = await this.fileHandle(key, true);
    if (!h) throw new Error("Local storage (OPFS) is unavailable");
    const writable = await h.createWritable();
    await writable.write(data as FileSystemWriteChunkType);
    await writable.close();
  }

  async list(prefix?: string): Promise<string[]> {
    const base = await this.dir(false, []);
    if (!base) return [];
    const out: string[] = [];
    const walk = async (
      handle: FileSystemDirectoryHandle,
      rel: string,
    ): Promise<void> => {
      // entries() exists on FileSystemDirectoryHandle in supporting browsers.
      for await (const [name, child] of (
        handle as unknown as {
          entries(): AsyncIterable<[string, FileSystemHandle]>;
        }
      ).entries()) {
        const childRel = rel ? `${rel}/${name}` : name;
        if (child.kind === "directory") {
          await walk(child as FileSystemDirectoryHandle, childRel);
        } else {
          out.push(childRel);
        }
      }
    };
    await walk(base, "");
    return prefix ? out.filter((k) => k.startsWith(prefix)) : out;
  }

  async delete(key: string): Promise<void> {
    const { dirs, file } = this.split(key);
    const dir = await this.dir(false, dirs);
    await dir?.removeEntry(file).catch(() => {});
  }
}
